import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/context/AuthContext';
import { grantInitialLeaves } from '@/api/employee';
import type {
  LeaveAllocationState,
  LeaveAllocationMethod,
} from '@/types/employee';
import { QueryKeys } from '@/constants/query-keys';
import { TranslationNS } from '@/constants/translation-namespaces';

export function useLeaveAllocation(employeeId?: string) {
  const { companyId } = useAuth();
  const queryClient = useQueryClient();
  const { t } = useTranslation(TranslationNS.Employee);

  const [allocationState, setAllocationState] = useState<LeaveAllocationState>(
    {}
  );

  const grantMutation = useMutation({
    mutationFn: (payload: {
      employeeId: string;
      allocations: {
        policyLeaveTypeId: string;
        method: LeaveAllocationMethod;
        customValue?: number;
      }[];
    }) => grantInitialLeaves(companyId!, payload),

    onSuccess: () => {
      toast.success(t('employee.toast_message.allocation_saved'));

      queryClient.invalidateQueries({
        queryKey: [QueryKeys.EligibleLeavePolicies],
      });

      queryClient.invalidateQueries({
        queryKey: [QueryKeys.EligibleLeavePoliciesUpdate],
      });
    },
  });

  const buildAllocations = () =>
    Object.entries(allocationState)
      .filter(([, v]) => v.method !== 'NONE')
      .map(([policyLeaveTypeId, v]) => ({
        policyLeaveTypeId,
        method: v.method,
        ...(v.method === 'CUSTOM' &&
          v.customValue !== undefined && {
            customValue: v.customValue,
          }),
      }));

  const saveAllocations = async (empId?: string) => {
    const id = empId || employeeId;
    if (!id) return;

    const allocations = buildAllocations();

    if (!allocations.length) return;

    await grantMutation.mutateAsync({
      employeeId: id,
      allocations,
    });
  };

  return {
    allocationState,
    setAllocationState,
    buildAllocations,
    saveAllocations,
    isSaving: grantMutation.isPending,
  };
}
